import {
  breakageHttp,
  BACKOFFICE_PREFIX,
  REDEMPTIONS_PREFIX,
} from "config/service/breakage-http-service";
import { handlerError } from "utils";
import {
  mockBalanceLots,
  mockEntitlementEnabled,
  mockSummary,
  paginateMock,
} from "services/breakage-mock-data";
import {
  buildRedemptionRequestBody,
  isValidRedemptionRequest,
  normalizeBalanceLot,
  normalizeBalanceLotPage,
} from "views/breakage/breakage-adapters";
import {
  buildBalanceLotsListParams,
  buildBalanceLotsSearchBody,
  buildBalanceLotsSearchPageParams,
  classifyEntitlementResponse,
  hasCadastralBalanceLotFilter,
} from "views/breakage/breakage-entitlement-status";
import { resolveExportFileName } from "views/breakage/breakage-export-download";

const emptyPage = () => ({
  items: [],
  page: {
    limit: 0,
    hasMore: false,
    nextCursor: null,
  },
});

const mockDelay = (ms = 450) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Mock só quando explicitamente ligado.
 * Sem API URL (e sem mock) → modo indisponível (UI deve mostrar erro, não inventar dados).
 */
export const isBreakageMockMode = () =>
  process.env.REACT_APP_BREAKAGE_USE_MOCK === "true";

export const isBreakageApiConfigured = () =>
  Boolean(process.env.REACT_APP_BREAKAGE_API_URL);

export const getBreakageIntegrationStatus = () => {
  if (isBreakageMockMode()) {
    return { mode: "mock", available: true };
  }
  if (!isBreakageApiConfigured()) {
    return {
      mode: "unavailable",
      available: false,
      reason: "REACT_APP_BREAKAGE_API_URL não configurada",
    };
  }
  return { mode: "api", available: true };
};

const apiUnavailableResult = (extra = {}) => ({
  ok: false,
  unavailable: true,
  status: 0,
  data: null,
  error: "Breakage API não configurada",
  ...extra,
});

export const getMyProductEntitlement = async () => {
  if (isBreakageMockMode()) {
    await mockDelay();
    const raw = {
      ok: true,
      status: 200,
      data: { ...mockEntitlementEnabled },
      unavailable: false,
    };
    return { ...raw, ...classifyEntitlementResponse(raw) };
  }

  if (!isBreakageApiConfigured()) {
    const raw = {
      ok: false,
      unavailable: true,
      data: null,
      error: "Breakage API não configurada",
    };
    return { ...raw, ...classifyEntitlementResponse(raw) };
  }

  try {
    const { data, status } = await breakageHttp.get(
      `${BACKOFFICE_PREFIX}/me/product-entitlement`
    );
    const raw = {
      ok: true,
      status: status || 200,
      data,
      unavailable: false,
    };
    return { ...raw, ...classifyEntitlementResponse(raw) };
  } catch (ex) {
    const status = ex?.response?.status;
    handlerError(ex, false);
    const raw = {
      ok: false,
      unavailable: status !== 401 && status !== 403,
      data: null,
      status,
      error: "Falha ao consultar entitlement",
    };
    return { ...raw, ...classifyEntitlementResponse(raw) };
  }
};

export const getBreakageSummary = async () => {
  if (isBreakageMockMode()) {
    await mockDelay();
    return {
      ok: true,
      data: {
        ...mockSummary,
        asOf: new Date().toISOString(),
      },
    };
  }

  if (!isBreakageApiConfigured()) {
    return apiUnavailableResult();
  }

  try {
    // Sem asOf: API atual não modela snapshot histórico e o summary
    // permanece BLOCKED_BY_DOMAIN_DECISION (503) até fórmula aprovada.
    const { data } = await breakageHttp.get(`${BACKOFFICE_PREFIX}/summary`);
    return { ok: true, data, status: 200 };
  } catch (ex) {
    const status = ex?.response?.status || 0;
    handlerError(ex);
    const domainBlocked =
      status === 503 &&
      (ex?.response?.data?.code === "BLOCKED_BY_DOMAIN_DECISION" ||
        ex?.response?.data?.extensions?.code === "BLOCKED_BY_DOMAIN_DECISION");
    return {
      ok: false,
      data: null,
      status,
      domainBlocked,
      error: domainBlocked
        ? "Summary financeiro ainda não disponível (definição de domínio pendente)."
        : "Falha ao carregar summary",
    };
  }
};

export const listBalanceLots = async ({
  limit = 10,
  cursor,
  participantId,
  legalName,
  documentNumber,
  eligibility,
  agingCondition,
  expiresFrom,
  expiresTo,
  creditedFrom,
  creditedTo,
} = {}) => {
  const exactParticipantId = participantId
    ? String(participantId).trim()
    : undefined;
  const exactLegalName = legalName ? String(legalName).trim() : undefined;
  const exactDocumentNumber = documentNumber
    ? String(documentNumber).replace(/[^A-Za-z0-9]/g, "").toUpperCase()
    : undefined;

  if (isBreakageMockMode()) {
    await mockDelay();
    let items = [...mockBalanceLots];
    if (exactParticipantId) {
      items = items.filter(
        (x) => x.participantId === String(exactParticipantId)
      );
    }
    if (exactLegalName) {
      const prefix = exactLegalName.toLowerCase();
      items = items.filter((x) =>
        String(x.participant?.legalName || "")
          .toLowerCase()
          .startsWith(prefix)
      );
    }
    if (exactDocumentNumber) {
      items = items.filter(
        (x) =>
          String(x.participant?.documentNumber || "")
            .replace(/[^A-Za-z0-9]/g, "")
            .toUpperCase() === exactDocumentNumber
      );
    }
    if (eligibility) {
      items = items.filter((x) => x.eligibility === eligibility);
    }
    if (agingCondition) {
      items = items.filter((x) => x.agingCondition === agingCondition);
    }
    if (creditedFrom) {
      const fromMs = new Date(creditedFrom).getTime();
      items = items.filter(
        (x) => new Date(x.creditedAt).getTime() >= fromMs
      );
    }
    if (creditedTo) {
      const toMs = new Date(creditedTo).getTime();
      items = items.filter((x) => new Date(x.creditedAt).getTime() < toMs);
    }
    if (expiresFrom) {
      const fromMs = new Date(expiresFrom).getTime();
      items = items.filter((x) => new Date(x.expiresAt).getTime() >= fromMs);
    }
    if (expiresTo) {
      const toMs = new Date(expiresTo).getTime();
      items = items.filter((x) => new Date(x.expiresAt).getTime() < toMs);
    }
    return {
      ok: true,
      data: normalizeBalanceLotPage(paginateMock(items, { limit, cursor })),
    };
  }

  if (!isBreakageApiConfigured()) {
    return { ...apiUnavailableResult(), data: emptyPage() };
  }

  try {
    const useSearch = hasCadastralBalanceLotFilter({
      participantId: exactParticipantId,
      legalName: exactLegalName,
      documentNumber: exactDocumentNumber,
    });

    // POST search exige filtro cadastral no body; listagem sem filtro usa GET.
    // Paginação (limit/cursor) sempre na query. documentNumber nunca na query (410).
    const { data } = useSearch
      ? await breakageHttp.post(
          `${BACKOFFICE_PREFIX}/balance-lots/search`,
          buildBalanceLotsSearchBody({
            participantId: exactParticipantId,
            legalName: exactLegalName,
            documentNumber: exactDocumentNumber,
            eligibility,
            agingCondition,
            expiresFrom,
            expiresTo,
            creditedFrom,
            creditedTo,
          }),
          { params: buildBalanceLotsSearchPageParams({ limit, cursor }) }
        )
      : await breakageHttp.get(`${BACKOFFICE_PREFIX}/balance-lots`, {
          params: buildBalanceLotsListParams({
            limit,
            cursor,
            eligibility,
            agingCondition,
            expiresFrom,
            expiresTo,
            creditedFrom,
            creditedTo,
          }),
        });

    return {
      ok: true,
      data: normalizeBalanceLotPage(data),
      status: 200,
    };
  } catch (ex) {
    handlerError(ex);
    const status = ex?.response?.status || 0;
    const code =
      ex?.response?.data?.code ||
      ex?.response?.data?.title ||
      ex?.response?.data?.error;
    const cadastralLimit =
      status === 422 && code === "CADASTRAL_SEARCH_LIMIT_EXCEEDED";
    const statusHint = status
      ? ` (HTTP ${status}${code ? `, ${code}` : ""})`
      : "";
    return {
      ok: false,
      data: emptyPage(),
      status,
      code: code || undefined,
      error: cadastralLimit
        ? "Busca cadastral excedeu o limite. Refine nome ou documento e tente novamente."
        : `Falha ao listar saldos${statusHint}`,
    };
  }
};

const EXPORT_FORMATS = new Set(["xlsx", "csv", "json", "xml"]);

const headerValue = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === "function") {
    return headers.get(name) || headers.get(name.toLowerCase());
  }
  return headers[name] || headers[name.toLowerCase()];
};

const readBlobErrorMessage = async (blob) => {
  if (!(blob instanceof Blob)) {
    return null;
  }
  try {
    const text = await blob.text();
    if (!text) return null;
    try {
      const parsed = JSON.parse(text);
      return (
        parsed?.detail ||
        parsed?.title ||
        parsed?.error ||
        parsed?.message ||
        null
      );
    } catch {
      return text.slice(0, 200);
    }
  } catch {
    return null;
  }
};

/**
 * Downloads BalanceLots as a server-generated file.
 * POST /balance-lots/export — body: format + same allowlisted filters as search.
 * Does not accept limit/cursor: the API returns all matching rows (hard cap 5.000).
 */
export const exportBalanceLots = async ({
  format = "xlsx",
  participantId,
  legalName,
  documentNumber,
  eligibility,
  agingCondition,
  expiresFrom,
  expiresTo,
  creditedFrom,
  creditedTo,
} = {}) => {
  const normalizedFormat = String(format || "xlsx").toLowerCase();
  if (!EXPORT_FORMATS.has(normalizedFormat)) {
    return {
      ok: false,
      blob: null,
      fileName: null,
      contentType: null,
      truncated: false,
      rowCount: null,
      error: "Formato de exportação inválido.",
      status: 400,
    };
  }

  const filters = buildBalanceLotsSearchBody({
    participantId: participantId ? String(participantId).trim() : undefined,
    legalName: legalName ? String(legalName).trim() : undefined,
    documentNumber: documentNumber
      ? String(documentNumber).replace(/[^A-Za-z0-9]/g, "").toUpperCase()
      : undefined,
    eligibility,
    agingCondition,
    expiresFrom,
    expiresTo,
    creditedFrom,
    creditedTo,
  });

  if (isBreakageMockMode()) {
    await mockDelay(200);
    const mockBody =
      normalizedFormat === "json"
        ? JSON.stringify({ meta: { mock: true }, items: mockBalanceLots }, null, 2)
        : "balanceLotId,participantId\nmock-1,participant-1\n";
    const blob = new Blob([mockBody], {
      type:
        normalizedFormat === "json"
          ? "application/json; charset=utf-8"
          : "text/csv; charset=utf-8",
    });
    return {
      ok: true,
      blob,
      fileName: `saldos_breakage_mock.${normalizedFormat === "xlsx" ? "csv" : normalizedFormat}`,
      contentType: blob.type,
      truncated: false,
      rowCount: mockBalanceLots.length,
      status: 200,
    };
  }

  if (!isBreakageApiConfigured()) {
    return {
      ...apiUnavailableResult(),
      blob: null,
      fileName: null,
      contentType: null,
      truncated: false,
      rowCount: null,
    };
  }

  try {
    const response = await breakageHttp.post(
      `${BACKOFFICE_PREFIX}/balance-lots/export`,
      { format: normalizedFormat, ...filters },
      { responseType: "blob" }
    );

    const disposition = headerValue(response.headers, "content-disposition");
    const contentType =
      headerValue(response.headers, "content-type") ||
      response.data?.type ||
      null;
    const truncatedRaw = headerValue(response.headers, "x-export-truncated");
    const rowCountRaw = headerValue(response.headers, "x-export-row-count");

    return {
      ok: true,
      blob: response.data,
      fileName: resolveExportFileName(disposition, normalizedFormat),
      contentType,
      truncated: String(truncatedRaw || "").toLowerCase() === "true",
      rowCount: rowCountRaw != null ? Number(rowCountRaw) : null,
      status: response.status || 200,
    };
  } catch (ex) {
    handlerError(ex, false);
    const status = ex?.response?.status || 0;
    const blobMessage = await readBlobErrorMessage(ex?.response?.data);
    const code =
      ex?.response?.data?.code ||
      ex?.response?.data?.title ||
      ex?.response?.data?.error;
    const cadastralLimit =
      status === 422 &&
      (code === "CADASTRAL_SEARCH_LIMIT_EXCEEDED" ||
        /CADASTRAL_SEARCH_LIMIT_EXCEEDED/i.test(blobMessage || ""));
    const statusHint = status
      ? ` (HTTP ${status}${code ? `, ${code}` : ""})`
      : "";
    return {
      ok: false,
      blob: null,
      fileName: null,
      contentType: null,
      truncated: false,
      rowCount: null,
      status,
      code: code || undefined,
      error: cadastralLimit
        ? "Busca cadastral excedeu o limite. Refine nome ou documento e tente novamente."
        : blobMessage || `Falha ao exportar saldos${statusHint}`,
    };
  }
};

export const listBreakages = async ({
  limit = 50,
  cursor,
  status,
  ownerCompanyId,
  participantId,
  createdFrom,
  createdTo,
  paymentId,
  internalTransactionId,
  accountingStatus,
  sort,
} = {}) => {
  if (isBreakageMockMode()) {
    return { ok: true, data: emptyPage() };
  }

  if (!isBreakageApiConfigured()) {
    return { ...apiUnavailableResult(), data: emptyPage() };
  }

  try {
    const { data } = await breakageHttp.get(`${BACKOFFICE_PREFIX}/breakages`, {
      params: {
        limit,
        cursor,
        status,
        ownerCompanyId,
        participantId,
        createdFrom,
        createdTo,
        paymentId,
        internalTransactionId,
        accountingStatus,
        sort,
      },
    });
    return { ok: true, data };
  } catch (ex) {
    handlerError(ex);
    return {
      ok: false,
      data: emptyPage(),
      status: ex?.response?.status || 0,
    };
  }
};

export const getBreakage = async (breakageProcessId) => {
  if (isBreakageMockMode()) {
    return { ok: true, data: null };
  }

  if (!isBreakageApiConfigured()) {
    return apiUnavailableResult();
  }

  try {
    const { data } = await breakageHttp.get(
      `${BACKOFFICE_PREFIX}/breakages/${breakageProcessId}`
    );
    return { ok: true, data };
  } catch (ex) {
    handlerError(ex);
    return {
      ok: false,
      data: null,
      status: ex?.response?.status || 0,
    };
  }
};

export const listBreakageBalanceLots = async (
  breakageProcessId,
  { limit = 50, cursor } = {}
) => {
  if (isBreakageMockMode()) {
    return {
      ok: true,
      data: normalizeBalanceLotPage(
        paginateMock(mockBalanceLots, { limit, cursor })
      ),
    };
  }

  if (!isBreakageApiConfigured()) {
    return { ...apiUnavailableResult(), data: emptyPage() };
  }

  try {
    const { data } = await breakageHttp.get(
      `${BACKOFFICE_PREFIX}/breakages/${breakageProcessId}/balance-lots`,
      { params: { limit, cursor } }
    );
    return { ok: true, data: normalizeBalanceLotPage(data) };
  } catch (ex) {
    handlerError(ex);
    return {
      ok: false,
      data: emptyPage(),
      status: ex?.response?.status || 0,
    };
  }
};

export const listBreakageAudit = async (
  breakageProcessId,
  { limit = 50, cursor, occurredFrom, occurredTo } = {}
) => {
  if (isBreakageMockMode()) {
    return { ok: true, data: emptyPage() };
  }

  if (!isBreakageApiConfigured()) {
    return { ...apiUnavailableResult(), data: emptyPage() };
  }

  try {
    const { data } = await breakageHttp.get(
      `${BACKOFFICE_PREFIX}/breakages/${breakageProcessId}/audit`,
      { params: { limit, cursor, occurredFrom, occurredTo } }
    );
    return { ok: true, data };
  } catch (ex) {
    handlerError(ex);
    return {
      ok: false,
      data: emptyPage(),
      status: ex?.response?.status || 0,
    };
  }
};

export const listParticipantBalanceLots = async (
  participantId,
  { limit = 50, cursor } = {}
) => {
  if (isBreakageMockMode()) {
    const items = mockBalanceLots.filter(
      (x) => x.participantId === String(participantId)
    );
    return {
      ok: true,
      data: normalizeBalanceLotPage(paginateMock(items, { limit, cursor })),
    };
  }

  if (!isBreakageApiConfigured()) {
    return { ...apiUnavailableResult(), data: emptyPage() };
  }

  try {
    const { data } = await breakageHttp.get(
      `${BACKOFFICE_PREFIX}/participants/${participantId}/balance-lots`,
      { params: { limit, cursor } }
    );
    return { ok: true, data: normalizeBalanceLotPage(data) };
  } catch (ex) {
    handlerError(ex);
    return {
      ok: false,
      data: emptyPage(),
      status: ex?.response?.status || 0,
    };
  }
};

export const getBalanceLot = async (balanceLotId) => {
  if (isBreakageMockMode()) {
    await mockDelay();
    const lot =
      mockBalanceLots.find((x) => x.balanceLotId === balanceLotId) || null;
    return { ok: Boolean(lot), data: normalizeBalanceLot(lot) };
  }

  if (!isBreakageApiConfigured()) {
    return apiUnavailableResult();
  }

  try {
    const { data } = await breakageHttp.get(
      `${BACKOFFICE_PREFIX}/balance-lots/${balanceLotId}`
    );
    return { ok: true, data: normalizeBalanceLot(data) };
  } catch (ex) {
    handlerError(ex);
    return {
      ok: false,
      data: null,
      status: ex?.response?.status || 0,
    };
  }
};

const createIdempotencyKey = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `redemption-${Date.now()}`;

/**
 * Solicita Redemption. Rejeita placeholders.
 * 2xx = aceitação da solicitação; não implica liquidação financeira.
 */
export const requestRedemption = async (body, { idempotencyKey } = {}) => {
  const payload = buildRedemptionRequestBody(body || {});

  if (!isValidRedemptionRequest(payload)) {
    return {
      ok: false,
      status: 0,
      data: null,
      rejected: true,
      error:
        "Solicitação incompleta: informe breakageOperationId e conta destino válidos (placeholders não são aceitos).",
    };
  }

  const key = idempotencyKey || createIdempotencyKey();

  if (isBreakageMockMode()) {
    await mockDelay(600);
    return {
      ok: true,
      status: 202,
      accepted: true,
      settled: false,
      idempotencyKey: key,
      data: {
        redemptionId: "mock-redemption-1",
        businessState: "REDEMPTION_REQUESTED",
        createdAt: new Date().toISOString(),
        mock: true,
        request: payload,
      },
    };
  }

  if (!isBreakageApiConfigured()) {
    return apiUnavailableResult();
  }

  try {
    const response = await breakageHttp.post(
      REDEMPTIONS_PREFIX,
      payload,
      {
        headers: {
          "Idempotency-Key": key,
        },
        validateStatus: (status) => status >= 200 && status < 300,
      }
    );
    const accepted =
      response.status >= 200 && response.status < 300;
    return {
      ok: accepted,
      status: response.status,
      accepted,
      settled: false,
      idempotencyKey: key,
      data: response.data,
    };
  } catch (ex) {
    handlerError(ex);
    return {
      ok: false,
      status: ex?.response?.status || 0,
      accepted: false,
      settled: false,
      data: ex?.response?.data || null,
      error: "Falha ao solicitar resgate",
    };
  }
};

export const getRedemption = async (redemptionId) => {
  if (isBreakageMockMode()) {
    await mockDelay();
    return {
      ok: true,
      data: {
        redemptionId,
        businessState: "REDEMPTION_REQUESTED",
        createdAt: new Date().toISOString(),
        mock: true,
      },
    };
  }

  if (!isBreakageApiConfigured()) {
    return apiUnavailableResult();
  }

  try {
    const { data } = await breakageHttp.get(
      `${REDEMPTIONS_PREFIX}/${encodeURIComponent(redemptionId)}`
    );
    return { ok: true, data };
  } catch (ex) {
    handlerError(ex);
    return {
      ok: false,
      data: null,
      status: ex?.response?.status || 0,
    };
  }
};

export const listRedemptions = async ({ limit = 50, cursor } = {}) => {
  if (isBreakageMockMode()) {
    await mockDelay();
    return { ok: true, data: { items: [], page: { limit, hasMore: false, nextCursor: null } } };
  }
  if (!isBreakageApiConfigured()) return apiUnavailableResult({ data: emptyPage() });
  try {
    const { data } = await breakageHttp.get(REDEMPTIONS_PREFIX, {
      params: { limit, cursor },
    });
    return { ok: true, data };
  } catch (ex) {
    handlerError(ex);
    return {
      ok: false,
      data: emptyPage(),
      status: ex?.response?.status || 0,
      error: "Falha ao consultar resgates",
    };
  }
};
